import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// The default export of hooks/useAxiosAuth is a FACTORY that builds a fresh
// axios instance, not a React hook — it calls nothing from React. Imported
// under its own name it trips eslint react-hooks/rules-of-hooks, because the
// rule reads `useX()` inside a non-component (a thunk body) as a hook called
// outside a render. Renaming it at the import is the whole fix.
import createAuthedAxios from "../hooks/useAxiosAuth";

// The laboratory, in one slice: the benches and the test menu the whole module
// reads before it can measure anything (master data), and the signed document
// that comes out the other end (reports). Two routers on the backend —
// /api/lab is one mount point shared by labMasterRoutes and labReportRoutes —
// and one slice here, because it is one department: the person who edits a
// reference band is the person who has to explain the flag it produced on a
// report a clinician is holding.
//
// TENANCY. Every endpoint answers a question about ONE hospital's laboratory,
// and there is no meaningful cross-hospital view of a test menu or a reference
// range: a band belongs to the METHOD a particular lab runs. A tenant admin,
// doctor or employee carries their hospital in the verified token and cannot
// name another, so the `tenantId` these thunks accept is ignored for them;
// Super Admin is deliberately unscoped everywhere in this codebase and MUST
// name the organisation or the request comes back 400. Passing it through
// unconditionally is what lets one screen serve both, instead of the pages
// growing a second code path that only Super Admin ever exercises.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

/**
 * A rejection the SCREEN can act on, not merely display.
 *
 * A laboratory refusal is almost never "something went wrong": it is "three
 * results are not verified, and here they are by name" (409 RESULTS_NOT_VERIFIED),
 * "this test is a component of two panels and would keep being measured inside
 * them" (409 TEST_IN_PANEL, with the panels attached), "the person who verified
 * this has no registration number" (409 VERIFIER_NOT_REGISTERED), or "that
 * version has been corrected — send this one instead" (409 REPORT_SUPERSEDED).
 * Every one of those has a different next action at the bench, and a bare
 * message string forces the technician to work it out from prose.
 *
 * BOTH PLACES THE BACKEND PUTS THAT DETAIL ARE CARRIED. labReportController
 * nests it under `data` ({ outstanding }, { supersededBy }, { signedById });
 * labMasterController spreads it at the TOP LEVEL of the body instead
 * ({ tests }, { panels }, { nested }, { missing }, { openResults }). Reading
 * only one of the two would silently drop half the module's actionable
 * refusals — which reads on screen as a bare "Failed to withdraw the test" for
 * the exact cases the message was written to explain. `detail` is the whole
 * body, so a screen can reach either without this file having to enumerate
 * every code the backend will ever add.
 */
const rejectionDetail = (error) => ({
    error: getSerializableError(error),
    code: error?.response?.data?.code || null,
    permission: error?.response?.data?.permission || null,
    data: error?.response?.data?.data || null,
    detail: error?.response?.data || null,
});

// Empty values are dropped rather than sent as `?sectionId=`. The controllers
// guard with truthiness so an empty string is harmless today, but `?status=` on
// a filtered list is one refactor away from meaning "status equals the empty
// string" and returning nothing, which reads on screen as "this lab has no
// tests" — a wrong answer delivered confidently.
const query = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        qs.append(key, Array.isArray(value) ? value.join(',') : String(value));
    });
    const s = qs.toString();
    return s ? `?${s}` : '';
};

// The same idea for a POST body. The controllers read the organisation as
// `req.body?.tenantId || req.query?.tenantId`, so a key that is present and empty
// is harmless TODAY — but it is also a claim ("this request names a tenant") that
// the body would be making falsely, and the next reader of a payload log has no
// way to tell it from a Super Admin who genuinely sent one. Absent means absent.
const withTenant = (body, tenantId) => (tenantId ? { ...body, tenantId } : body);

// ========================================================== sections ========

/** The benches, each with how much of the menu it actually owns. */
export const getSections = createAsyncThunk(
    'lab/getSections',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { status = '', search = '', tenantId = '' } = params;
            const response = await axiosInstance.get(`/lab/sections${query({ status, search, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const getSection = createAsyncThunk(
    'lab/getSection',
    async (sectionId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/sections/${sectionId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const createSection = createAsyncThunk(
    'lab/createSection',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/sections`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Note for the form: PUT will not accept status='inactive'. Closing a bench
// goes through deactivateSection, which refuses while tests are still worked
// there — a section is the QUEUE a sample lands in, and closing one that still
// owns tests does not move that work anywhere, it only makes the tab it was
// visible on disappear while samples keep arriving in it.
export const updateSection = createAsyncThunk(
    'lab/updateSection',
    async ({ sectionId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/lab/sections/${sectionId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// DELETE closes the bench; it never removes the row, because every released
// report quotes the section it was printed under. A 409 SECTION_IN_USE carries
// the count on `detail.tests`, so the screen can offer to reassign them.
export const deactivateSection = createAsyncThunk(
    'lab/deactivateSection',
    async (sectionId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/lab/sections/${sectionId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ============================================================= tests ========

/**
 * The test master. Leaving `isActive` empty returns withdrawn tests as well as
 * live ones, which is the master screen's correct default — a test is withdrawn
 * rather than deleted, and a list that hid them would leave an administrator
 * unable to find the row they need to bring back.
 *
 * Every row carries `needsReferenceRange` and `needsComponents`: the two
 * configuration gaps that are invisible until they surface in front of a
 * patient — a numeric test with no band reports unflagged for ever, and an
 * empty panel is refused at the doctor's order screen with PANEL_EMPTY.
 */
export const getTests = createAsyncThunk(
    'lab/getTests',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                search = '', sectionId = '', sampleType = '', resultType = '', isPanel = '',
                isOutsourced = '', partnerId = '', isActive = '', page = 1, limit = 25, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/lab/tests${query({ search, sectionId, sampleType, resultType, isPanel, isOutsourced, partnerId, isActive, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The doctor's order-screen type-ahead. Ranked exact-code, then prefix, then
 * infix, and every hit already carries what the consultation room needs to
 * decide: the tube and cap colour, the price, the turnaround, the patient
 * preparation — and, for a profile, the analytes inside it.
 *
 * The components travel with the hit rather than behind a second request
 * because the commonest ordering mistake is a standalone analyte ticked an hour
 * after a profile that already contains it: one measurement, two lines on the
 * bill, and a patient bled for nothing. Under two characters the backend
 * answers with an empty list and a `message` saying so — kept as `searchHint`
 * rather than dropped, because an empty dropdown with no explanation reads as
 * "this lab does not offer that test".
 */
export const searchTests = createAsyncThunk(
    'lab/searchTests',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { q = '', limit = 20, tenantId = '' } = params;
            const response = await axiosInstance.get(`/lab/tests/search${query({ q, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** One test with its bands, its components, the profiles it belongs to, and
 *  what those components would cost ordered one by one. */
export const getTest = createAsyncThunk(
    'lab/getTest',
    async (testId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/tests/${testId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const createTest = createAsyncThunk(
    'lab/createTest',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/tests`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * PUT will not accept isActive=false — withdrawing goes through deactivateTest,
 * which checks for unfinished results first.
 *
 * THE ANSWER IS WORTH READING EVEN WHEN IT SUCCEEDS. Changing the unit or the
 * method comes back 200 with `warnings`: the reference bands are NOT restated
 * by the edit, so a creatinine band left in µmol/L against a method now
 * reporting mg/dL flags every patient in the hospital as critically high. The
 * warnings are stored in state precisely because a screen that only reads
 * `message` would throw that away at the one moment somebody is looking.
 */
export const updateTest = createAsyncThunk(
    'lab/updateTest',
    async ({ testId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/lab/tests/${testId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Withdraws the test from the menu; the row survives so an archived report can
// still name its own contents. Refused with TEST_HAS_OPEN_RESULTS while samples
// are on the bench (withdrawing does not stop them being measured, only being
// explained) and with TEST_IN_PANEL while a profile still contains it.
export const deactivateTest = createAsyncThunk(
    'lab/deactivateTest',
    async (testId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/lab/tests/${testId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ================================================== reference ranges ========

/**
 * A test's bands, youngest first.
 *
 * PASS `gender` AND `ageDays` (or `dob`, optionally with `collectedAt`) TO SEE
 * WHICH BAND WOULD WIN. The answer is computed by the backend calling the same
 * labService.pickReferenceRange the result path calls, and the screen must
 * never reimplement it: "most specific wins" has three tie-breaks, and a
 * preview that drifted from the engine would leave a report whose flag
 * contradicts its own reference column, with no way for a clinician to tell
 * which half is wrong.
 *
 * Age is resolved at COLLECTION, never at reporting — a sample drawn the day
 * before a birthday and verified a week later must be interpreted as the
 * patient was when they were bled.
 */
export const getRanges = createAsyncThunk(
    'lab/getRanges',
    async ({ testId, gender = '', ageDays = '', dob = '', collectedAt = '', tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(
                `/lab/tests/${testId}/ranges${query({ gender, ageDays, dob, collectedAt, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Add a band. Refused as INERT_RANGE when it carries neither a bound nor a
 * textNormal (a row that looks like configuration and can never flag anything),
 * and as CRITICAL_BOUND_INSIDE_RANGE when a panic threshold sits inside the
 * normal interval — which would make every healthy patient critical and a phone
 * call for each one, until the bench stops believing the alerts.
 *
 * Leave `low` / `high` / `criticalLow` / `criticalHigh` EMPTY, never zero, for
 * "unbounded at this end": a zero substituted for a missing high flags every
 * result in the hospital.
 */
export const createRange = createAsyncThunk(
    'lab/createRange',
    async ({ testId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/tests/${testId}/ranges`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Editing a band does not restate results already recorded against it — every
// result snapshots the interval it was flagged against — which is why this call
// needs no in-use check and why the confirmation says so.
export const updateRange = createAsyncThunk(
    'lab/updateRange',
    async ({ testId, rangeId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/lab/tests/${testId}/ranges/${rangeId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// A real delete, unlike the masters: the snapshot on each result means removing
// a band cannot restate a released report. `warnings` says so when the test is
// left with none at all — from then on its results go out unflagged and nothing
// on the bench announces it.
export const deleteRange = createAsyncThunk(
    'lab/deleteRange',
    async ({ testId, rangeId }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/lab/tests/${testId}/ranges/${rangeId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ==================================================== panel components ======

/** What a profile is made of, with the panel's price beside the sum of its
 *  parts — a profile priced above its components is a tariff error nobody reads
 *  a price list carefully enough to catch. */
export const getPanel = createAsyncThunk(
    'lab/getPanel',
    async ({ testId, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/tests/${testId}/panel${query({ tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Set what a profile contains. Two body shapes, one endpoint:
 *
 *   { componentTestId, sortOrder }   add ONE analyte
 *   { components: [...] }            REPLACE the whole composition
 *
 * The editor should send the replace form, and send it ONCE. Saving a reorder
 * as a delete-then-add sequence of requests leaves the profile half-built if
 * the browser is closed in the middle — orderable, priced as a profile, and
 * measuring three of its five analytes.
 *
 * Refused with NOT_A_PANEL (mark the test as a panel first — that changes what
 * is measured and billed, so it is never a side effect of opening this tab),
 * PANEL_NESTED, PANEL_SELF_REFERENCE, DUPLICATE_COMPONENT and PANEL_EMPTY.
 */
export const setPanelComponents = createAsyncThunk(
    'lab/setPanelComponents',
    async ({ testId, ...body }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/tests/${testId}/panel`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const removePanelComponent = createAsyncThunk(
    'lab/removePanelComponent',
    async ({ testId, componentTestId }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/lab/tests/${testId}/panel/${componentTestId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================== partners ========
// Behind a SECOND tenant feature ('lab_outsourcing'), so these five calls come
// back 403 for a hospital that runs everything in-house. Guard the screen with
// <FeatureGuard control="lab_outsourcing"> rather than letting the list render
// and fail — a referral tab that always errors is indistinguishable from a
// broken one.

export const getPartners = createAsyncThunk(
    'lab/getPartners',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { status = '', search = '', tenantId = '' } = params;
            const response = await axiosInstance.get(`/lab/partners${query({ status, search, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** One reference lab with both sides of the money on it: what the hospital
 *  charges for each referred test and what it pays the partner. */
export const getPartner = createAsyncThunk(
    'lab/getPartner',
    async (partnerId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/partners/${partnerId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// A partner saved without a contact person, a phone number or an NABL number
// comes back 201 WITH warnings. They are not address-book decoration: an
// overdue referral shows nothing on the local worklist because nothing is
// pending locally, and the only way to chase it is to telephone a named human.
export const createPartner = createAsyncThunk(
    'lab/createPartner',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/partners`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const updatePartner = createAsyncThunk(
    'lab/updatePartner',
    async ({ partnerId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/lab/partners/${partnerId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Stops new referrals; the row survives so archived reports can still name the
// lab that performed the test. Refused with PARTNER_IN_USE while active
// outsourced tests point here — deactivating would not redirect that work, it
// would only hide who the tubes are being couriered to.
export const deactivatePartner = createAsyncThunk(
    'lab/deactivatePartner',
    async (partnerId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/lab/partners/${partnerId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ============================================================ orders ========

/**
 * A doctor orders tests. One order, its items, and NO TUBE: the sample row and
 * the accession number are both minted at COLLECTION, because an order that is
 * never drawn must not burn a number out of the year's sequence and leave a hole
 * an auditor asks about (models/labSampleModel.js).
 *
 * Body: { patientId, doctorId?, appointmentId?, priority?, testIds: [],
 * clinicalNotes?, tenantId? }, priority one of routine / urgent / stat.
 *
 * A profile goes in `testIds` as ITS OWN id and is expanded server-side
 * (services/lab/labService.expandPanel). Sending the components instead would
 * bill five analytes at their standalone prices and lose the heading the report
 * groups them under. A profile with nothing in it is refused with PANEL_EMPTY —
 * it is orderable and priced, and it would measure nothing.
 */
export const createLabOrder = createAsyncThunk(
    'lab/createLabOrder',
    async ({ tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/orders`, withTenant(body, tenantId));
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================== worklist ========

/**
 * WHAT IS ON THE BENCH RIGHT NOW.
 *
 * A query, never a table. The moment "what is waiting" is stored rather than
 * derived it can disagree with the samples and results it claims to summarise,
 * and a laboratory reading a stale queue leaves a tube in a rack all afternoon.
 *
 * `bucket` is a stage of physical custody, not a status invented for a dropdown:
 * pending_collection (nobody has drawn it), collected (drawn, in transit),
 * received (the lab owes a number), resulted (a number exists, a second person
 * must approve it), verified (approved, waiting to be released). The answer
 * carries `counts` for every BUCKET at once, so a screen can put "eleven waiting
 * to be verified" on a tab without asking five times. It holds nothing else —
 * there is no `counts.critical`; how many panic values a row carries is on the
 * row itself, as `criticalCount`.
 *
 * `date` CUTS THE LIST TO ONE CALENDAR DAY, and whether to send it is the
 * caller's decision rather than this file's, because the two screens that ask
 * are answering different questions. The phlebotomy room works a day at a time
 * and wants it. The bench worklist withholds it on every bucket but the
 * completed-work register, and is right to: a queue of UNDONE work bounded by a
 * date is a queue that hides its own backlog, and a tube ordered yesterday and
 * still not drawn is the most important row a laboratory can be shown. Pass it
 * empty to mean "no day filter" — an empty value is dropped rather than sent as
 * `?date=`, which is a filter matching nothing.
 */
export const getWorklist = createAsyncThunk(
    'lab/getWorklist',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                bucket = '', search = '', date = '', sectionId = '',
                page = 1, limit = 25, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/lab/worklist${query({ bucket, search, date, sectionId, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// =========================================================== samples ========

/**
 * THE TUBE NOW EXISTS. This is the call that mints the accession number.
 *
 * ADDRESSED BY orderId, NOT BY sampleId, and that is not an oversight: before
 * collection there IS no sample row to name. The number comes from a per-tenant,
 * per-year counter inside the same transaction (labService.nextAccessionNo), so
 * two phlebotomists pressing collect in the same second cannot produce the same
 * label — which is a mislabelled tube, and a mislabelled tube is the one error in
 * a laboratory that reaches the patient with nothing left to catch it.
 *
 * Body: { orderId, sampleType?, containerType?, collectionSite?, tenantId? },
 * collectionSite one of lab / home / ward / otc. The specimen and container are
 * sent back from what the screen actually showed the person who drew it, rather
 * than left to the server's defaults, so the row records the tube in their hand
 * and not the tube the master assumed.
 *
 * THERE IS NO `remarks` HERE, deliberately — the note about the draw belongs to
 * receiveSample, which appends it to the specimen. A screen that offered a
 * remarks box on this call would be collecting a sentence about a difficult draw
 * and throwing it away.
 *
 * THE CALLER MUST READ `data.accessionNo` BACK AND SHOW IT. A collection
 * recorded without its number reaching the screen is an unlabelled tube, which is
 * a rejection and a second needle.
 */
export const collectSample = createAsyncThunk(
    'lab/collectSample',
    async ({ tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/samples/collect`, withTenant(body, tenantId));
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Booked in at the bench — the moment the laboratory takes over the turnaround
 * clock. Separate from the draw even when the same person does both a metre
 * apart, because the gap between collectedAt and receivedAt is the
 * pre-analytical delay: potassium leaks out of cells in a tube left on a
 * trolley, and a lab that cannot see its ward runs take three hours will never
 * work out why its potassiums read high.
 */
export const receiveSample = createAsyncThunk(
    'lab/receiveSample',
    async ({ sampleId, remarks = '', tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/lab/samples/${sampleId}/receive`,
                withTenant({ remarks }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The specimen is unusable and the patient must be drawn again.
 *
 * `rejectionReason` is mandatory and is free text rather than four options,
 * because "haemolysed", "clotted", "insufficient volume" and "unlabelled tube"
 * lead to four different conversations — with the ward, with the phlebotomist,
 * or with nobody. It is also the row a NABL auditor reads and the answer the
 * patient is owed for a second needle.
 *
 * A rejection is not an undo. Nothing here revives the tube: a recollection is a
 * NEW sample with a NEW number, and the rejected row stays so "how often do we
 * redraw, and from which ward" remains answerable.
 */
export const rejectSample = createAsyncThunk(
    'lab/rejectSample',
    async ({ sampleId, rejectionReason, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/lab/samples/${sampleId}/reject`,
                withTenant({ rejectionReason }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// =========================================================== results ========

/**
 * ONE ORDER'S BENCH: the order, the patient, the specimen, the viewer, every
 * analyte on it, and the critical alerts it has raised.
 *
 * `viewer` is the half a screen cannot compute for itself — who this user is,
 * and whether they carry a registration number. A pathology report is signed, so
 * a verifier without one is refused (VERIFIER_NOT_REGISTERED); learning that
 * before anything is typed is the difference between a warning and a wasted
 * shift.
 *
 * Each result carries a `range` block ALONGSIDE its snapshotted refLow/refHigh.
 * The snapshot is what was judged and what prints; the block carries the CRITICAL
 * bounds, which the result row does not keep — without it a screen cannot preview
 * a panic value and must say so rather than colouring a lethal potassium the same
 * green as a normal one.
 */
export const getOrderResults = createAsyncThunk(
    'lab/getOrderResults',
    async ({ orderId, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/orders/${orderId}/results${query({ tenantId })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * RECORDING measurements. It publishes nothing and approves nothing — it is a
 * number being written down, and the state machine the backend enforces
 * (pending -> entered -> verified -> released) will not let this call skip a
 * step.
 *
 * Body: { orderId, results: [{ resultId, resultValue, remarks }], tenantId? }.
 * `numericValue` is deliberately NOT sent. Turning '<0.01', '1,240' or 'Not
 * detected' into a comparable number is one decision and it belongs beside the
 * flagging that depends on it; two implementations of that parse is how a '<0.01'
 * becomes 0.01 on a report.
 *
 * READ `data.criticalAlerts` ON THE WAY BACK. A panic value is a telephone call,
 * not a colour: the backend raises a lab_critical_alerts row so there is a record
 * of who was told and when, and the screen has to put that in front of somebody
 * who must act on it now — not in a toast that slides away while the technician
 * is looking at the analyser.
 */
export const enterResults = createAsyncThunk(
    'lab/enterResults',
    async ({ tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/results/enter`, withTenant(body, tenantId));
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * THE SECOND PERSON. Verifying approves what is STORED, never what is on the
 * screen — so a caller must refuse to send this while an edit is unsaved, or a
 * technician who typed 14.2 and pressed verify would be putting their name to
 * the 9.6 that is still in the database.
 *
 * Refused with VERIFIER_NOT_REGISTERED when the signatory has no registration
 * number on file. Verifying one's own entry is NOT refused: a one-technician
 * laboratory legitimately exists, and blocking it produces two people sharing a
 * login, which destroys the audit trail the rule was protecting. It is recorded
 * as a single-operator verification and printed as a footnote instead.
 */
export const verifyResults = createAsyncThunk(
    'lab/verifyResults',
    async ({ orderId, resultIds, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/lab/results/verify`,
                withTenant({ orderId, resultIds }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Correcting a number the patient may already be holding.
 *
 * This does not overwrite anything. A NEW result row is issued, the old one stays
 * beside it, and the amendment needs verifying like any other value — because the
 * released report that quoted the original still exists in the world and an
 * inquiry will ask for exactly that version.
 *
 * `amendReason` is the clinically important half: it prints on the amended report
 * and it is what a reader is told about why the number changed.
 */
export const amendResult = createAsyncThunk(
    'lab/amendResult',
    async ({ resultId, resultValue, amendReason, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/lab/results/amend`,
                withTenant({ resultId, resultValue, amendReason }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ==================================================== critical alerts =======

/**
 * THE PANIC-VALUE CALL LOG — the open work items on the laboratory's board.
 *
 * An alert row is created the instant a result flags critical, with informedTo /
 * informedBy / informedAt EMPTY, and that emptiness is the point: it stays open
 * until a human closes it by naming the person they actually spoke to. Sending a
 * notification is not the obligation; being able to show years later WHO was
 * told, BY WHOM and WHEN is.
 *
 * `acknowledged` DEFAULTS TO 'false' ON THE SERVER, so asking for nothing asks
 * for the board — the open items — which is the answer somebody opening this
 * screen wants. Pass 'true' for the closed log. Everything on a row is a SNAPSHOT
 * taken at the moment of the alert: a critical result is the one most likely to
 * be amended afterwards, and the log must keep saying what was actually called
 * through, not what the value was later corrected to.
 */
export const getAlerts = createAsyncThunk(
    'lab/getAlerts',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                acknowledged = '', orderId = '', patientId = '',
                page = 1, limit = 25, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/lab/alerts${query({ acknowledged, orderId, patientId, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Closing the alert: the call was made, and this is who answered it.
 *
 * `informedTo` is a NAME in words, not a staffId, because the person told is
 * frequently outside this system — the ward sister, the referring doctor on
 * their mobile. "The lab informed the ward" is not a record, it is a claim, so
 * both ends of the conversation go on the row: the caller comes from the verified
 * token and is never accepted from the client.
 *
 * Body: { informedTo, method?, notes?, tenantId? }, method one of phone /
 * in_person / message / email.
 */
export const ackAlert = createAsyncThunk(
    'lab/ackAlert',
    async ({ alertId, informedTo, method = 'phone', notes = '', tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/lab/alerts/${alertId}/ack`,
                withTenant({ informedTo, method, notes }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// =========================================================== reports ========

/**
 * THE CLINICAL ACT: a set of verified numbers becomes a signed document.
 *
 * Body: { orderId, interpretation?, tenantId? }. Nothing else — the version,
 * the report number, the signatory and whether this is an amendment are all
 * decided by the server FROM THE DATA, because a client-supplied flag could
 * disagree with the rows and the version number on the page would then be wrong
 * in the one place it matters most.
 *
 * WHAT THE SCREEN MUST HANDLE, because none of it is a generic error:
 *   409 RESULTS_NOT_VERIFIED — `data.outstanding` names each unverified test.
 *       The person pressing release is the person who can go and get them
 *       verified, so list them; "3 results outstanding" sends them hunting.
 *   409 VERIFIER_NOT_REGISTERED — the verifier has no registration number on
 *       file. An unsigned pathology report is not a report; this is the last
 *       point at which one can be stopped from leaving the building.
 *   409 VERIFIER_UNKNOWN / NOTHING_TO_RELEASE / NO_RESULTS / ORDER_CANCELLED /
 *       NOT_A_LAB_ORDER — states of the order, each with its own next step.
 *   409 RELEASE_RACED — somebody else released the same set a moment ago.
 *       Reload the order; do not retry blindly.
 *
 * ON SUCCESS, TWO FIELDS THE SCREEN MUST NOT SWALLOW. `singleOperator` lists
 * results whose verifier was also their enterer — legitimate in a
 * one-technician lab and never blocked, but it is printed as a footnote on the
 * document and should be visible to the person releasing it. `pdfAvailable`
 * false means the report IS released and only the drawing failed; the button
 * should offer to retry the PDF, not the release.
 */
export const releaseReport = createAsyncThunk(
    'lab/releaseReport',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/reports/release`, payload);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** The report register. Every row carries `supersededBy` when a corrected
 *  version exists — a list showing version 1 and version 2 with nothing to
 *  separate them is the sheet somebody photographs, and the withdrawn one is
 *  exactly the one a reader in a hurry picks. */
export const getReports = createAsyncThunk(
    'lab/getReports',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                search = '', patientId = '', orderId = '', status = '',
                from = '', to = '', page = 1, limit = 20, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/lab/reports${query({ search, patientId, orderId, status, from, to, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * One document: the report row, its printed result lines, the identity block
 * (patient, specimen, referring doctor), the section headings and panel names
 * the lines group under, and `supersededBy` when a later version exists.
 *
 * The values, units, reference bands, methods and flags on those lines are
 * SNAPSHOTS taken when each result was entered — the screen must print them as
 * given and never re-derive a flag from the current master, or a report
 * reopened in three years would show today's reference range beside a value
 * judged against the one in force when it was measured.
 */
export const getReport = createAsyncThunk(
    'lab/getReport',
    async (reportId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/reports/${reportId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * A signed link to the report PDF, drawn on first request and cached.
 *
 * The cached copy is the document AS ISSUED, which is what should normally be
 * handed over — a correction is a new version with its own number, never a
 * rewrite of this one. `regenerate: true` produces a copy stamped DUPLICATE and
 * is refused to patients, who must not be able to mint fresh renderings of a
 * medical record on demand.
 *
 * A 503 PDF_UNAVAILABLE means THE REPORT IS RELEASED and only the drawing
 * failed. Two things follow. The backend's own message says exactly that and is
 * passed through rather than replaced with a house string that would not; and
 * because hooks/useAxiosAuth routes every status >= 503 to the global
 * server-error handler, the page must show this slice's message beside whatever
 * that overlay says — otherwise the lab concludes the release did not happen
 * and releases the order a second time.
 */
export const getReportPdf = createAsyncThunk(
    'lab/getReportPdf',
    async ({ reportId, regenerate = false }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/reports/${reportId}/pdf${query({ regenerate: regenerate ? 'true' : '' })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Send the patient their report again. This RE-SENDS a document; it never
 * re-issues one — same row, same number, same version, same PDF. Two documents
 * in the world for one set of measurements is a state no clinician holding both
 * could resolve.
 *
 * `recipient` is optional and wins when given, which is the common case of a
 * patient whose email was never captured reading it out at the counter; without
 * one on file the call comes back NO_RECIPIENT asking for it. A version that
 * has since been corrected is refused with REPORT_SUPERSEDED and
 * `data.supersededBy` names the one to send instead — re-sending a withdrawn
 * value in a message that looks exactly like the first one is worse than not
 * sending at all.
 */
export const resendReport = createAsyncThunk(
    'lab/resendReport',
    async ({ reportId, channel = 'email', recipient = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/lab/reports/${reportId}/resend`, { channel, recipient });
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * One patient's reports — RELEASED ONES ONLY, whoever is asking, and that is
 * the rule rather than a convenience for the patient screen. A draft is the
 * laboratory's working copy: it may hold a value about to be repeated,
 * corrected or withdrawn, and anyone who needs to see work in progress opens
 * the ORDER, where its unreleased state is unmistakable.
 *
 * Rows carry `pdfAvailable` and never the S3 key; the download goes through
 * getReportPdf, which re-checks who is asking and hands back a URL that expires.
 */
export const getPatientReports = createAsyncThunk(
    'lab/getPatientReports',
    async ({ patientId, page = 1, limit = 20, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/lab/patients/${patientId}/reports${query({ page, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The writes, listed rather than pattern-matched.
 *
 * staffSlice tests the action type against a regex, which works there because
 * no read is named after a write. Here `getReport`, `getReportPdf` and
 * `resendReport` all contain "Report", `getPanel` and `setPanelComponents`
 * differ only by their verb, and `getRanges` sits beside `deleteRange` — so
 * /report|panel|range|set|delete/ would put the whole screen behind a saving
 * spinner every time somebody merely opened a document. Naming the thunks is
 * the version that cannot go wrong when the next one is added.
 */
const MUTATIONS = [
    createSection, updateSection, deactivateSection,
    createTest, updateTest, deactivateTest,
    createRange, updateRange, deleteRange,
    setPanelComponents, removePanelComponent,
    createPartner, updatePartner, deactivatePartner,
    createLabOrder,
    collectSample, receiveSample, rejectSample,
    enterResults, verifyResults, amendResult,
    ackAlert,
    releaseReport, resendReport,
];
const MUTATION_TYPES = new Set(MUTATIONS.map((thunk) => thunk.typePrefix));
const matchMutation = (phase) => (action) => {
    const cut = action.type.lastIndexOf('/');
    return cut > 0 && action.type.slice(cut + 1) === phase && MUTATION_TYPES.has(action.type.slice(0, cut));
};

const labSlice = createSlice({
    name: "lab",
    initialState: {
        sections: [],
        sectionsLoading: false,
        selectedSection: null,

        tests: [],
        testsTotal: 0,
        testsTotalPages: 1,
        testsLoading: false,
        selectedTest: null,
        searchResults: [],
        // What the backend said about a search that returned nothing useful
        // ("Type at least two characters"). An empty dropdown with no reason
        // beside it reads as "this lab does not offer that test".
        searchHint: null,
        searchLoading: false,

        ranges: [],
        // Which test the bands on screen belong to. A band is meaningless
        // without one, and a stale list left over from the previous test is a
        // reference interval attached to the wrong analyte.
        rangesTestId: null,
        rangesLoading: false,
        // The band that WOULD be picked for the previewed patient, straight
        // from the engine the result path uses. null is a real answer — "this
        // lab holds no reference for that patient", which is the gap somebody
        // then fixes — so `rangesPreviewed` says whether a preview was asked
        // for at all. Collapsing the two would show "no band matches" on every
        // plain list.
        rangesResolvedId: null,
        rangesPreviewed: false,

        panel: null,
        panelLoading: false,

        partners: [],
        partnersLoading: false,
        selectedPartner: null,

        // ---- the work in progress ----
        worklist: [],
        // Every bucket's size at once, plus `critical`. Kept whole rather than
        // unpacked into five fields: the tabs need all of them on screen at all
        // times, and "eleven waiting to be verified" is the sentence that decides
        // how the next hour is spent.
        worklistCounts: {},
        worklistTotal: 0,
        worklistTotalPages: 1,
        worklistLoading: false,

        // One order's bench, whole: { order, patient, sample, viewer, results,
        // alerts }. Held as the server sent it because every part of it is
        // answering a question about the same specimen, and splitting it invites
        // a render where the patient header has moved on and the values have not.
        orderResults: null,
        orderResultsLoading: false,
        // Raised by the last enterResults. Not folded into `warnings`: a warning
        // is advice, this is a telephone call somebody has to make now.
        lastCriticalAlerts: [],
        // What collectSample minted. The number goes on the tube, so it is kept
        // rather than toasted — a toast disappears and the tube is still blank.
        lastCollection: null,

        alerts: [],
        alertsTotal: 0,
        alertsTotalPages: 1,
        alertsLoading: false,

        reports: [],
        reportsTotal: 0,
        reportsTotalPages: 1,
        reportsLoading: false,
        selectedReport: null,
        reportLoading: false,
        // Short-lived by design: the bucket is private and the URL is
        // presigned, so this is a handle for the print button now, never
        // something to persist or share.
        reportPdf: null,

        patientReports: [],
        patientReportsTotal: 0,
        patientReportsTotalPages: 1,
        patientReportsLoading: false,

        lastRelease: null,
        // Kept apart from `error`: a refused release is a structured answer the
        // bench acts on (which results are unverified, which signatory is
        // unregistered), not a banner to dismiss. See rejectionDetail.
        releaseError: null,

        // Advisories that arrive on a SUCCESSFUL write — a unit changed under
        // existing bands, a panel left with no components, a partner with no
        // phone number. They are legal states, so they cannot be refusals; they
        // are also invisible everywhere else, so a screen that only rendered
        // `message` would drop them at the one moment anybody is looking.
        warnings: [],

        mutating: false,
        error: null,
    },
    reducers: {
        clearLabError: (state) => { state.error = null; },
        clearReleaseError: (state) => { state.releaseError = null; },
        clearWarnings: (state) => { state.warnings = []; },
        // Dismissing the panic-value dialog. The ALERT is not cleared by this —
        // it is closed on the server by naming who was telephoned (ackAlert).
        // This only stops the same dialog reappearing on the next render.
        clearCriticalAlerts: (state) => { state.lastCriticalAlerts = []; },
        // The accession number has been written on the tube.
        clearCollection: (state) => { state.lastCollection = null; },
        // Between two tests in the master editor. The catalogue itself survives
        // on purpose — it is the same menu for the next row, and refetching it
        // makes the screen blink for no reason.
        resetTestEditor: (state) => {
            state.selectedTest = null;
            state.ranges = [];
            state.rangesTestId = null;
            state.rangesResolvedId = null;
            state.rangesPreviewed = false;
            state.panel = null;
            state.warnings = [];
        },
        resetLabState: (state) => {
            state.sections = [];
            state.selectedSection = null;
            state.tests = [];
            state.selectedTest = null;
            state.searchResults = [];
            state.searchHint = null;
            state.ranges = [];
            state.rangesTestId = null;
            state.rangesResolvedId = null;
            state.rangesPreviewed = false;
            state.panel = null;
            state.partners = [];
            state.selectedPartner = null;
            state.worklist = [];
            state.worklistCounts = {};
            state.orderResults = null;
            state.lastCriticalAlerts = [];
            state.lastCollection = null;
            state.alerts = [];
            state.reports = [];
            state.selectedReport = null;
            state.reportPdf = null;
            state.patientReports = [];
            state.lastRelease = null;
            state.releaseError = null;
            state.warnings = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ---- sections ----
            .addCase(getSections.pending, (state) => { state.sectionsLoading = true; state.error = null; })
            .addCase(getSections.fulfilled, (state, action) => {
                state.sectionsLoading = false;
                state.sections = action.payload?.data || [];
            })
            .addCase(getSections.rejected, (state, action) => {
                state.sectionsLoading = false;
                state.error = action.payload?.error || 'Failed to load the lab sections';
            })
            .addCase(getSection.fulfilled, (state, action) => { state.selectedSection = action.payload; })

            // ---- tests ----
            .addCase(getTests.pending, (state) => { state.testsLoading = true; state.error = null; })
            .addCase(getTests.fulfilled, (state, action) => {
                state.testsLoading = false;
                state.tests = action.payload?.data || [];
                state.testsTotal = action.payload?.total || 0;
                state.testsTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getTests.rejected, (state, action) => {
                state.testsLoading = false;
                state.error = action.payload?.error || 'Failed to load the test master';
            })

            .addCase(searchTests.pending, (state) => { state.searchLoading = true; })
            .addCase(searchTests.fulfilled, (state, action) => {
                state.searchLoading = false;
                state.searchResults = action.payload?.data || [];
                state.searchHint = action.payload?.message || null;
            })
            // A failed type-ahead never raises the page banner. The doctor is
            // mid-keystroke; the next one supersedes it, and a toast per
            // character is noise that trains people to ignore the real ones.
            .addCase(searchTests.rejected, (state) => { state.searchLoading = false; })

            .addCase(getTest.fulfilled, (state, action) => { state.selectedTest = action.payload; })

            // ---- reference ranges ----
            .addCase(getRanges.pending, (state) => {
                state.rangesLoading = true;
                // The previous test's bands go before the next test's arrive.
                // Leaving them up means an editor can save a paediatric
                // interval against whatever analyte was open a moment ago.
                state.ranges = [];
                state.rangesResolvedId = null;
                state.rangesPreviewed = false;
                state.error = null;
            })
            .addCase(getRanges.fulfilled, (state, action) => {
                state.rangesLoading = false;
                state.ranges = action.payload?.data || [];
                state.rangesTestId = action.payload?.testId || null;
                // Present-and-null and absent mean different things — see the
                // note on rangesResolvedId in initialState.
                state.rangesPreviewed = Object.prototype.hasOwnProperty.call(action.payload || {}, 'resolvedRangeId');
                state.rangesResolvedId = state.rangesPreviewed ? (action.payload.resolvedRangeId ?? null) : null;
            })
            .addCase(getRanges.rejected, (state, action) => {
                state.rangesLoading = false;
                state.error = action.payload?.error || 'Failed to load the reference ranges';
            })

            // ---- panel components ----
            .addCase(getPanel.pending, (state) => { state.panelLoading = true; })
            .addCase(getPanel.fulfilled, (state, action) => {
                state.panelLoading = false;
                state.panel = action.payload || null;
            })
            .addCase(getPanel.rejected, (state, action) => {
                state.panelLoading = false;
                state.error = action.payload?.error || 'Failed to load the panel';
            })

            // ---- partners ----
            .addCase(getPartners.pending, (state) => { state.partnersLoading = true; state.error = null; })
            .addCase(getPartners.fulfilled, (state, action) => {
                state.partnersLoading = false;
                state.partners = action.payload?.data || [];
            })
            .addCase(getPartners.rejected, (state, action) => {
                state.partnersLoading = false;
                state.error = action.payload?.error || 'Failed to load the reference labs';
            })
            .addCase(getPartner.fulfilled, (state, action) => { state.selectedPartner = action.payload; })

            // ---- worklist ----
            // The rows are NOT cleared on pending. This list refreshes itself
            // every twenty seconds on a screen the bench keeps open all day, and
            // emptying it between polls would make the whole queue blink — which
            // is how people stop reading it. A caller that wants a first-load
            // spinner should gate it on an empty list, not on this flag.
            .addCase(getWorklist.pending, (state) => { state.worklistLoading = true; state.error = null; })
            .addCase(getWorklist.fulfilled, (state, action) => {
                state.worklistLoading = false;
                state.worklist = action.payload?.data || [];
                state.worklistCounts = action.payload?.counts || {};
                state.worklistTotal = action.payload?.total || 0;
                state.worklistTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getWorklist.rejected, (state, action) => {
                state.worklistLoading = false;
                state.error = action.payload?.error || 'Failed to load the worklist';
            })

            // ---- one order's bench ----
            .addCase(getOrderResults.pending, (state) => {
                state.orderResultsLoading = true;
                // The previous order goes before the next one arrives. Leaving it
                // up means somebody can type a value into one patient's row under
                // another patient's name for as long as the request takes.
                state.orderResults = null;
                state.error = null;
            })
            .addCase(getOrderResults.fulfilled, (state, action) => {
                state.orderResultsLoading = false;
                state.orderResults = action.payload || null;
                state.alerts = action.payload?.alerts || [];
            })
            .addCase(getOrderResults.rejected, (state, action) => {
                state.orderResultsLoading = false;
                state.error = action.payload?.error || 'Failed to load this order';
            })

            // ---- samples ----
            .addCase(collectSample.fulfilled, (state, action) => {
                state.lastCollection = action.payload?.data || action.payload || null;
            })

            // ---- results ----
            // Kept apart from `warnings` on purpose — see lastCriticalAlerts in
            // initialState. A caller that renders only `warnings` would drop the
            // one answer somebody has to act on within the minute.
            .addCase(enterResults.fulfilled, (state, action) => {
                state.lastCriticalAlerts = action.payload?.data?.criticalAlerts
                    || action.payload?.criticalAlerts
                    || [];
            })

            // ---- critical alerts ----
            .addCase(getAlerts.pending, (state) => { state.alertsLoading = true; })
            .addCase(getAlerts.fulfilled, (state, action) => {
                state.alertsLoading = false;
                state.alerts = action.payload?.data || [];
                state.alertsTotal = action.payload?.total || 0;
                state.alertsTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getAlerts.rejected, (state, action) => {
                state.alertsLoading = false;
                state.error = action.payload?.error || 'Failed to load the critical alerts';
            })

            // ---- reports ----
            .addCase(getReports.pending, (state) => { state.reportsLoading = true; state.error = null; })
            .addCase(getReports.fulfilled, (state, action) => {
                state.reportsLoading = false;
                state.reports = action.payload?.data || [];
                state.reportsTotal = action.payload?.total || 0;
                state.reportsTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getReports.rejected, (state, action) => {
                state.reportsLoading = false;
                state.error = action.payload?.error || 'Failed to load the reports';
            })

            .addCase(getReport.pending, (state) => {
                state.reportLoading = true;
                // The previous patient's document is dropped before the next
                // one arrives. Leaving it up means somebody can read one
                // patient's potassium under another patient's name for as long
                // as the request takes.
                state.selectedReport = null;
                state.reportPdf = null;
                state.error = null;
            })
            .addCase(getReport.fulfilled, (state, action) => {
                state.reportLoading = false;
                state.selectedReport = action.payload || null;
            })
            .addCase(getReport.rejected, (state, action) => {
                state.reportLoading = false;
                state.error = action.payload?.error || 'Failed to load the report';
            })

            .addCase(getReportPdf.pending, (state) => { state.reportPdf = null; })
            .addCase(getReportPdf.fulfilled, (state, action) => { state.reportPdf = action.payload || null; })
            .addCase(getReportPdf.rejected, (state, action) => {
                // PDF_UNAVAILABLE is not a failed release. The backend's
                // message already says the report is valid, so it is passed
                // through rather than replaced with a house string that would
                // not.
                state.error = action.payload?.error || 'Failed to produce the report PDF';
            })

            .addCase(getPatientReports.pending, (state) => { state.patientReportsLoading = true; })
            .addCase(getPatientReports.fulfilled, (state, action) => {
                state.patientReportsLoading = false;
                state.patientReports = action.payload?.data || [];
                state.patientReportsTotal = action.payload?.total || 0;
                state.patientReportsTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getPatientReports.rejected, (state, action) => {
                state.patientReportsLoading = false;
                state.error = action.payload?.error || 'Failed to load this patient’s reports';
            })

            .addCase(releaseReport.pending, (state) => { state.releaseError = null; })
            .addCase(releaseReport.fulfilled, (state, action) => {
                state.lastRelease = action.payload || null;
                // The document exists now, so the print button has something to
                // point at without a second round trip. Shaped like getReport's
                // payload so one component renders either, and supersededBy is
                // null because a report cannot be corrected before it is issued.
                if (action.payload?.report) {
                    state.selectedReport = {
                        report: action.payload.report,
                        results: action.payload.results || [],
                        supersededBy: null,
                    };
                }
            })
            .addCase(releaseReport.rejected, (state, action) => { state.releaseError = action.payload || null; })

            // Every mutation shares one flag; the pages refetch on success. The
            // warnings ride along on the same matcher, so a write that succeeds
            // with advice attached cannot lose it on the way to the screen.
            .addMatcher(matchMutation('pending'), (state) => {
                state.mutating = true;
                state.error = null;
                state.warnings = [];
            })
            .addMatcher(matchMutation('fulfilled'), (state, action) => {
                state.mutating = false;
                state.warnings = Array.isArray(action.payload?.warnings) ? action.payload.warnings : [];
            })
            .addMatcher(matchMutation('rejected'), (state, action) => {
                state.mutating = false;
                state.error = action.payload?.error || 'Action failed';
            });
    },
});

export const {
    clearLabError,
    clearReleaseError,
    clearWarnings,
    clearCriticalAlerts,
    clearCollection,
    resetTestEditor,
    resetLabState,
} = labSlice.actions;
export default labSlice.reducer;
